import React, { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
    Search, 
    Calendar, 
    Users, 
    Plane, 
    DollarSign, 
    CreditCard, 
    RefreshCw, 
    ChevronRight, 
    X, 
    CheckCircle2, 
    Clock, 
    AlertTriangle, 
    XCircle, 
    Info, 
    ExternalLink, 
    Heart, 
    User, 
    Shield, 
    Phone, 
    TrendingUp,
    Trash2,
    Pencil,
    Save,
    Plus,
    Send
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { sortExpeditions, EXPEDITION_ORDER } from '../../lib/expeditionOrder';


export interface Participant {
    fullName: string;
    passport: string;
    dob: string;
    shoeSize: string;
    clothingSize: string;
    diet: string;
    health: string;
    emergencyName: string;
    emergencyPhone: string;
    email?: string;
    selected_services?: string[];
}

export interface Booking {
    id: string;
    created_at: string;
    expedition_slug: string;
    start_date: string;
    people_count: number;
    total_price: number;
    airport_transfer: boolean;
    payment_status: string; // 'pending' | 'paid' | 'confirmed' | 'completed' | 'cancelled'
    paypal_order_id: string;
    participants: Participant[];
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    internal_notes?: string;
    waiver_accepted?: boolean;
    cancellation_policy_accepted?: boolean;
    created_by?: string;
}

const BookingsManager: React.FC = () => {
    const { theme, expeditions } = useOutletContext<{ theme: 'light' | 'dark'; expeditions: any[] }>();
    const isDark = theme === 'dark';

    const [bookings, setBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [expeditionFilter, setExpeditionFilter] = useState<string>('all');
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
    const [notification, setNotification] = useState<{ message: string, type: 'error' | 'success' } | null>(null);
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<Booking | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [editBooking, setEditBooking] = useState<Booking | null>(null);
    const [saving, setSaving] = useState(false);
    const [searchParams] = useSearchParams();

    // Manual CRM bookings states
    const [showNewBookingModal, setShowNewBookingModal] = useState(false);
    const [newBooking, setNewBooking] = useState<{
        expedition_slug: string;
        start_date: string;
        people_count: number;
        total_price: number;
        airport_transfer: boolean;
        payment_status: string;
        internal_notes: string;
        participants: Participant[];
        waiver_accepted: boolean;
        cancellation_policy_accepted: boolean;
    }>({
        expedition_slug: '',
        start_date: '',
        people_count: 1,
        total_price: 0,
        airport_transfer: false,
        payment_status: 'pending',
        internal_notes: '',
        participants: [
            {
                fullName: '',
                email: '',
                passport: '',
                dob: '',
                shoeSize: '',
                clothingSize: '',
                diet: '',
                health: '',
                emergencyName: '',
                emergencyPhone: ''
            }
        ],
        waiver_accepted: false,
        cancellation_policy_accepted: false
    });

    const calculatePrice = (slug: string, people: number, airport: boolean) => {
        const exp = (expeditions || []).find(e => e.id === slug);
        if (!exp) return 0;

        let perPerson = 0;
        let total = 0;

        if (exp.price_flat && exp.price_flat > 0) {
            perPerson = exp.price_flat;
            total = perPerson * people;
        } else {
            const pricingTiers = exp.pricing_tiers;
            if (!pricingTiers) {
                // Fallback to legacy hardcoded logic if no tiers defined
                if (exp.id === 'laguna') {
                    if (people === 1) perPerson = 170;
                    else if (people < 4) perPerson = 90;
                    else perPerson = 70;
                } else if (exp.id === 'horquetas') {
                    if (people === 1) perPerson = 900;
                    else if (people === 2) perPerson = 700;
                    else perPerson = 500;
                }
                total = perPerson * people;
            } else {
                // Dynamic tiered pricing
                const sortedTiers = [...pricingTiers].sort((a, b) => b.min_people - a.min_people);
                const applicableTier = sortedTiers.find(tier => people >= tier.min_people);
                if (applicableTier) {
                    perPerson = applicableTier.price_per_person;
                    total = perPerson * people;
                }
            }
        }

        if (airport) total += 50;
        return total;
    };

    const handleNewBookingFieldChange = (field: string, value: any) => {
        setNewBooking(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'expedition_slug' || field === 'people_count' || field === 'airport_transfer') {
                updated.total_price = calculatePrice(updated.expedition_slug, Number(updated.people_count), updated.airport_transfer);
            }
            if (field === 'people_count') {
                const count = Number(value);
                const currentParticipants = [...prev.participants];
                if (count > currentParticipants.length) {
                    for (let i = currentParticipants.length; i < count; i++) {
                        currentParticipants.push({
                            fullName: '',
                            email: '',
                            passport: '',
                            dob: '',
                            shoeSize: '',
                            clothingSize: '',
                            diet: '',
                            health: '',
                            emergencyName: '',
                            emergencyPhone: ''
                        });
                    }
                } else if (count < currentParticipants.length) {
                    currentParticipants.splice(count);
                }
                updated.participants = currentParticipants;
            }
            return updated;
        });
    };

    // Auto-set filter from URL query param (?filter=active)
    useEffect(() => {
        const filter = searchParams.get('filter');
        if (filter === 'active') {
            setStatusFilter('active');
        } else {
            setStatusFilter('all');
        }
    }, [searchParams]);

    // Auto-hide notification
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Fetch Bookings from Supabase
    const fetchBookings = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('bookings')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setBookings(data || []);
        } catch (err: any) {
            console.error('Error fetching bookings from Supabase:', err);
            setNotification({ 
                message: `Error al cargar reservas: ${err.message || err}`, 
                type: 'error' 
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, []);

    // Helper to get expedition name from slug
    const getExpedicionName = (slug: string) => {
        const exp = (expeditions || []).find(e => e.id === slug);
        return exp ? (exp.name_es || exp.name_en) : slug;
    };

    // Change status logic & email trigger
    const handleStatusChange = async (booking: Booking, newStatus: string) => {
        const oldStatus = booking.payment_status;
        if (oldStatus === newStatus) return;

        setUpdatingId(booking.id);
        try {
            // 1. Update in Supabase
            const { error } = await supabase
                .from('bookings')
                .update({ payment_status: newStatus })
                .eq('id', booking.id);

            if (error) throw error;

            // Update local state
            setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, payment_status: newStatus } : b));
            if (selectedBooking && selectedBooking.id === booking.id) {
                setSelectedBooking(prev => prev ? { ...prev, payment_status: newStatus } : null);
            }

            // 2. Trigger booking status email via Supabase Edge Function → Resend
            const leadParticipant = booking.participants?.[0];
            const customerName = leadParticipant?.fullName || 'Sin Nombre';
            const customerEmail = leadParticipant?.email;

            if (customerEmail) {
                try {
                    const response = await fetch(
                        'https://iuixlsqrlfkfnvnckgij.supabase.co/functions/v1/booking-status-email',
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                            },
                            body: JSON.stringify({
                                new_status: newStatus,
                                booking_id: booking.id,
                                customer_name: customerName,
                                customer_email: customerEmail,
                                booking_details: {
                                    expedition_slug: booking.expedition_slug,
                                    expedition_name: getExpedicionName(booking.expedition_slug),
                                    start_date: booking.start_date,
                                    people_count: booking.people_count,
                                    total_price: booking.total_price,
                                    airport_transfer: booking.airport_transfer,
                                    paypal_order_id: booking.paypal_order_id,
                                    participants: booking.participants
                                }
                            })
                        }
                    );
                    const result = await response.json();
                    if (result.skipped) {
                        console.log(`No email configured for status "${newStatus}" — skipped.`);
                    } else if (result.success) {
                        console.log(`✅ Email enviado. Template: ${result.template}, Resend ID: ${result.resend_id}`);
                    } else {
                        console.error('Edge Function error:', result);
                    }
                } catch (emailErr) {
                    console.error('Error calling booking-status-email Edge Function:', emailErr);
                }
            } else {
                console.warn(`Booking ${booking.id} no tiene email de contacto — correo de estado omitido.`);
            }

            setNotification({ 
                message: `Reserva actualizada a "${getStatusLabel(newStatus)}" con éxito.`, 
                type: 'success' 
            });
        } catch (err: any) {
            console.error('Error updating booking status:', err);
            setNotification({ 
                message: `Error al actualizar reserva: ${err.message || err}`, 
                type: 'error' 
            });
        } finally {
            setUpdatingId(null);
        }
    };

    // Delete booking
    const handleDeleteBooking = async (booking: Booking) => {
        setDeleting(true);
        try {
            const { error } = await supabase
                .from('bookings')
                .delete()
                .eq('id', booking.id);

            if (error) throw error;

            setBookings(prev => prev.filter(b => b.id !== booking.id));
            setDeleteConfirm(null);
            if (selectedBooking?.id === booking.id) setSelectedBooking(null);
            setNotification({ message: 'Reserva eliminada correctamente.', type: 'success' });
        } catch (err: any) {
            console.error('Error deleting booking:', err);
            setNotification({ message: `Error al eliminar: ${err.message || err}`, type: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    // Save edited booking to Supabase
    const handleSaveEdit = async () => {
        if (!editBooking) return;
        setSaving(true);
        try {
            const { error } = await supabase
                .from('bookings')
                .update({
                    start_date: editBooking.start_date,
                    people_count: editBooking.people_count,
                    total_price: editBooking.total_price,
                    airport_transfer: editBooking.airport_transfer,
                    participants: editBooking.participants,
                    internal_notes: editBooking.internal_notes || '',
                })
                .eq('id', editBooking.id);

            if (error) throw error;

            setBookings(prev => prev.map(b => b.id === editBooking.id ? { ...b, ...editBooking } : b));
            if (selectedBooking?.id === editBooking.id) setSelectedBooking({ ...selectedBooking, ...editBooking });
            setEditBooking(null);
            setNotification({ message: 'Reserva actualizada correctamente.', type: 'success' });
        } catch (err: any) {
            console.error('Error saving booking edit:', err);
            setNotification({ message: `Error al guardar: ${err.message || err}`, type: 'error' });
        } finally {
            setSaving(false);
        }
    };
    // Create manual booking in Supabase
    const handleCreateBooking = async (sendEmail: boolean) => {
        if (!newBooking.expedition_slug) {
            setNotification({ message: 'Por favor selecciona una expedición', type: 'error' });
            return;
        }
        if (!newBooking.start_date) {
            setNotification({ message: 'Por favor selecciona una fecha de salida', type: 'error' });
            return;
        }
        
        const lead = newBooking.participants[0];
        if (!lead || !lead.fullName.trim()) {
            setNotification({ message: 'El participante líder debe tener un nombre completo', type: 'error' });
            return;
        }

        setSaving(true);
        try {
            const manualPaypalId = `MANUAL-${Math.floor(Math.random() * 10000000)}`;

            const dbData = {
                expedition_slug: newBooking.expedition_slug,
                start_date: newBooking.start_date,
                people_count: Number(newBooking.people_count),
                total_price: Number(newBooking.total_price),
                airport_transfer: newBooking.airport_transfer,
                payment_status: newBooking.payment_status,
                paypal_order_id: manualPaypalId,
                participants: newBooking.participants,
                contact_name: lead.fullName,
                contact_email: lead.email || '',
                contact_phone: lead.emergencyPhone || '',
                internal_notes: newBooking.internal_notes,
                waiver_accepted: newBooking.waiver_accepted,
                cancellation_policy_accepted: newBooking.cancellation_policy_accepted,
                created_by: 'admin'
            };

            const { data, error } = await supabase
                .from('bookings')
                .insert([dbData])
                .select('*')
                .single();

            if (error) throw error;

            if (data) {
                setBookings(prev => [data, ...prev]);

                const customerName = lead.fullName;
                const customerEmail = lead.email;

                if (sendEmail && customerEmail && customerEmail.trim() !== '') {
                    try {
                        await fetch(
                            'https://iuixlsqrlfkfnvnckgij.supabase.co/functions/v1/booking-status-email',
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                                },
                                body: JSON.stringify({
                                    new_status: newBooking.payment_status,
                                    booking_id: data.id,
                                    customer_name: customerName,
                                    customer_email: customerEmail,
                                    booking_details: {
                                        expedition_slug: data.expedition_slug,
                                        expedition_name: getExpedicionName(data.expedition_slug),
                                        start_date: data.start_date,
                                        people_count: data.people_count,
                                        total_price: data.total_price,
                                        airport_transfer: data.airport_transfer,
                                        paypal_order_id: manualPaypalId,
                                        participants: data.participants
                                    }
                                })
                            }
                        );
                        console.log('✅ Correo de estado enviado exitosamente.');
                    } catch (emailErr) {
                        console.error('Error al enviar email de estado para nueva reserva manual:', emailErr);
                    }
                }

                // Reset state
                setNewBooking({
                    expedition_slug: '',
                    start_date: '',
                    people_count: 1,
                    total_price: 0,
                    airport_transfer: false,
                    payment_status: 'pending',
                    internal_notes: '',
                    participants: [
                        {
                            fullName: '',
                            email: '',
                            passport: '',
                            dob: '',
                            shoeSize: '',
                            clothingSize: '',
                            diet: '',
                            health: '',
                            emergencyName: '',
                            emergencyPhone: ''
                        }
                    ],
                    waiver_accepted: false,
                    cancellation_policy_accepted: false
                });
                setShowNewBookingModal(false);
                setNotification({ message: 'Reserva manual creada correctamente.', type: 'success' });
            }
        } catch (err: any) {
            console.error('Error al crear reserva manual:', err);
            setNotification({ message: `Error al crear: ${err.message || err}`, type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // UI labels & colors mapping
    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'pending': return 'Pendiente';
            case 'paid': return 'Pagado';
            case 'confirmed': return 'Confirmado';
            case 'completed': return 'Completado';
            case 'cancelled': return 'Cancelado';
            default: return status;
        }
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'pending':
                return 'bg-[#00d4ff]/10 text-[#00d4ff] border-[#00d4ff]/20';
            case 'partially_paid':
                return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
            case 'paid':
                return 'bg-[#25D366]/10 text-[#25D366] border-[#25D366]/20';
            case 'confirmed':
                return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
            case 'completed':
                return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
            case 'cancelled':
                return 'bg-red-500/10 text-red-500 border-red-500/20';
            default:
                return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending': return <Clock className="w-3.5 h-3.5" />;
            case 'partially_paid': return <CreditCard className="w-3.5 h-3.5" />;
            case 'paid': return <CheckCircle2 className="w-3.5 h-3.5" />;
            case 'confirmed': return <Shield className="w-3.5 h-3.5" />;
            case 'completed': return <CheckCircle2 className="w-3.5 h-3.5" />;
            case 'cancelled': return <XCircle className="w-3.5 h-3.5" />;
            default: return <Clock className="w-3.5 h-3.5" />;
        }
    };

    // Calculations for Stats Card
    const stats = React.useMemo(() => {
        const activeBookings = bookings.filter(b => b.payment_status !== 'cancelled');
        
        const totalCount = bookings.length;
        const totalRevenue = activeBookings.reduce((sum, b) => sum + (b.total_price || 0), 0);
        const confirmedPaid = bookings.filter(b => ['paid', 'confirmed', 'completed', 'partially_paid'].includes(b.payment_status)).length;
        const pendingCount = bookings.filter(b => b.payment_status === 'pending').length;

        return [
            { label: 'Reservas Totales', value: totalCount.toString(), icon: Users, color: '#00d4ff' },
            { label: 'Ingresos Netos', value: `USD$${totalRevenue.toLocaleString()}`, icon: DollarSign, color: '#25D366' },
            { label: 'Confirmadas / Pagadas', value: confirmedPaid.toString(), icon: CheckCircle2, color: '#3b82f6' },
            { label: 'Pendientes de Pago', value: pendingCount.toString(), icon: Clock, color: '#ffc107' }
        ];
    }, [bookings]);

    // Filtering logic
    const filteredBookings = bookings
        .filter(b => {
            const lead = b.participants?.[0];
            const name = lead?.fullName || '';
            const email = lead?.email || '';
            const paypal = b.paypal_order_id || '';
            const slug = b.expedition_slug || '';
            const searchLower = search.toLowerCase();

            return (
                name.toLowerCase().includes(searchLower) ||
                email.toLowerCase().includes(searchLower) ||
                paypal.toLowerCase().includes(searchLower) ||
                slug.toLowerCase().includes(searchLower) ||
                b.id.toLowerCase().includes(searchLower)
            );
        })
        .filter(b => {
            if (statusFilter === 'all') return true;
            if (statusFilter === 'active') return ['paid', 'confirmed', 'partially_paid'].includes(b.payment_status);
            return b.payment_status === statusFilter;
        })
        .filter(b => expeditionFilter === 'all' || b.expedition_slug === expeditionFilter);

    // List of unique slugs in bookings for filtering dropdown sorted geographically
    const uniqueSlugs = React.useMemo(() => {
        const list = Array.from(new Set(bookings.map(b => b.expedition_slug).filter(Boolean)));
        return list.sort((a, b) => {
            const expA = (expeditions || []).find(e => e.id === a || e.slug === a);
            const expB = (expeditions || []).find(e => e.id === b || e.slug === b);
            const orderA = expA ? (EXPEDITION_ORDER[expA.id] ?? 999) : 999;
            const orderB = expB ? (EXPEDITION_ORDER[expB.id] ?? 999) : 999;
            return orderA - orderB;
        });
    }, [bookings, expeditions]);

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className={`text-4xl font-title uppercase tracking-tighter ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        CRM de Reservas
                    </h1>
                    <p className={`text-[10px] uppercase tracking-[0.3em] font-bold mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Controla las expediciones contratadas, participantes y flujos de pago.
                    </p>
                </div>
                
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => setShowNewBookingModal(true)}
                        className="bg-[#00d4ff] hover:bg-[#00b8e6] text-black px-6 py-3 font-title text-xs uppercase tracking-widest flex items-center gap-2 transition-all rounded-[10px]"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Nueva Reserva
                    </button>
                    <button 
                        onClick={fetchBookings}
                        disabled={loading}
                        className={`border px-6 py-3 font-title text-xs uppercase tracking-widest flex items-center gap-2 hover:border-[#00d4ff] transition-all rounded-[10px] ${loading ? 'opacity-50' : ''} ${
                            isDark ? 'bg-black border-rock text-gray-400 hover:text-white' : 'bg-white border-gray-200 text-gray-500 hover:text-gray-900'
                        }`}
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Actualizar
                    </button>
                </div>
            </div>

            {/* Stats Dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, i) => (
                    <div key={i} className={`border p-6 rounded-[10px] relative overflow-hidden group transition-all duration-300 ${
                        isDark ? 'bg-[#1a1512] border-rock' : 'bg-white border-gray-200 shadow-sm'
                    }`}>
                        <div className="flex justify-between items-start mb-4 relative z-10">
                            <div className={`w-10 h-10 border rounded-[10px] flex items-center justify-center text-gray-400`}>
                                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                            </div>
                        </div>
                        <div className="relative z-10">
                            <p className="text-[9px] text-gray-500 font-bold uppercase tracking-[0.2em] mb-1">{stat.label}</p>
                            <p className={`text-2xl font-title tracking-tighter ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className={`flex flex-col sm:flex-row flex-wrap gap-4 items-center justify-start border p-4 rounded-[10px] transition-colors ${
                isDark ? 'bg-[#1a1512] border-rock' : 'bg-white border-gray-200 shadow-sm'
            }`}>
                {/* Status Dropdown Filter */}
                <div className="w-full sm:w-48 relative">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`w-full border px-4 py-3 text-xs outline-none focus:border-[#00d4ff] transition-all rounded-[10px] appearance-none cursor-pointer ${
                            isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                        }`}
                    >
                        <option value="all">Todos los Estados</option>
                        <option value="active">✅ Activas (Pagadas + Confirmadas)</option>
                        <option value="pending">⏳ Pendientes</option>
                        <option value="paid">💳 Pagadas</option>
                        <option value="confirmed">🛡️ Confirmadas</option>
                        <option value="completed">🏆 Completadas</option>
                        <option value="cancelled">❌ Canceladas</option>
                    </select>
                </div>

                {/* Expedition Dropdown Filter */}
                <div className="w-full sm:w-48 relative">
                    <select
                        value={expeditionFilter}
                        onChange={(e) => setExpeditionFilter(e.target.value)}
                        className={`w-full border px-4 py-3 text-xs outline-none focus:border-[#00d4ff] transition-all rounded-[10px] appearance-none cursor-pointer ${
                            isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                        }`}
                    >
                        <option value="all">Todas las Expediciones</option>
                        {uniqueSlugs.map(slug => (
                            <option key={slug} value={slug}>{getExpedicionName(slug)}</option>
                        ))}
                    </select>
                </div>

                {/* Text Search */}
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input 
                        type="text" 
                        placeholder="Buscar cliente, ID reserva, PayPal..." 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={`w-full border pl-11 pr-4 py-3 text-xs outline-none focus:border-[#00d4ff] transition-all rounded-[10px] ${
                            isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                        }`}
                    />
                </div>
            </div>

            {/* Bookings List Table */}
            <div className={`border rounded-[10px] overflow-hidden ${
                isDark ? 'bg-[#1a1512] border-rock' : 'bg-white border-gray-200 shadow-sm'
            }`}>
                {loading ? (
                    <div className="text-center py-20 flex flex-col items-center gap-3">
                        <RefreshCw className="w-8 h-8 animate-spin text-[#00d4ff]" />
                        <p className="text-gray-500 text-xs font-bold uppercase tracking-widest">Cargando base de datos...</p>
                    </div>
                ) : filteredBookings.length === 0 ? (
                    <div className="text-center py-24 text-gray-500 text-xs font-bold uppercase tracking-widest">
                        Ninguna reserva coincide con los criterios de búsqueda.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className={`border-b text-[9px] font-bold uppercase tracking-widest text-gray-500 ${
                                    isDark ? 'bg-black/30 border-rock' : 'bg-gray-50 border-gray-100'
                                }`}>
                                    <th className="px-6 py-4">Cliente / Lead</th>
                                    <th className="px-6 py-4">Expedición</th>
                                    <th className="px-6 py-4">Fecha Salida</th>
                                    <th className="px-6 py-4 text-center">Pers.</th>
                                    <th className="px-6 py-4 text-right">Inversión</th>
                                    <th className="px-6 py-4">Estado</th>
                                    <th className="px-6 py-4 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y text-xs ${isDark ? 'divide-rock/50' : 'divide-gray-100'}`}>
                                {filteredBookings.map((booking) => {
                                    const lead = booking.participants?.[0];
                                    const name = lead?.fullName || 'Sin Nombre';
                                    const email = lead?.email || 'Sin Email';
                                    const isUpdating = updatingId === booking.id;

                                    return (
                                        <tr key={booking.id} className={`transition-colors duration-200 group ${
                                            isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50/50'
                                        }`}>
                                            <td className="px-6 py-4">
                                                <div className={`font-bold uppercase tracking-tight group-hover:text-[#00d4ff] transition-colors ${
                                                    isDark ? 'text-white' : 'text-gray-900'
                                                }`}>{name}</div>
                                                <div className="text-[10px] text-gray-500 truncate max-w-[200px]">{email}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className={`font-bold uppercase truncate max-w-[220px] ${
                                                    isDark ? 'text-gray-300' : 'text-gray-700'
                                                }`}>
                                                    {getExpedicionName(booking.expedition_slug)}
                                                </div>
                                                <div className="text-[8px] font-mono text-gray-600 truncate max-w-[150px]">
                                                    PayPal ID: {booking.paypal_order_id || 'N/A'}
                                                </div>
                                            </td>
                                            <td className={`px-6 py-4 font-mono ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {new Date(booking.start_date).toLocaleDateString('es-CL', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </td>
                                            <td className={`px-6 py-4 text-center font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <div className="flex items-center justify-center gap-1.5">
                                                    <Users className="w-3.5 h-3.5 text-gray-500" />
                                                    {booking.people_count}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right font-title text-[#00d4ff] text-sm">
                                                USD${Number(booking.total_price || 0).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4">
                                                {isUpdating ? (
                                                    <div className="flex items-center gap-2 text-gray-500 text-[10px] uppercase font-bold tracking-widest animate-pulse">
                                                        <RefreshCw className="w-3 h-3 animate-spin text-[#00d4ff]" />
                                                        Guardando...
                                                    </div>
                                                ) : (
                                                    <div className="relative w-fit">
                                                        <select
                                                            value={booking.payment_status}
                                                            onChange={(e) => handleStatusChange(booking, e.target.value)}
                                                            disabled={updatingId === booking.id}
                                                            className={`px-3 py-1.5 pr-6 rounded-[10px] text-[8px] font-bold uppercase tracking-widest border outline-none cursor-pointer appearance-none flex items-center gap-1.5 font-sans ${getStatusStyle(booking.payment_status)} ${
                                                                isDark ? 'bg-black text-white' : 'bg-white text-gray-900 border-gray-300'
                                                            }`}
                                                            style={{
                                                                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E")`,
                                                                backgroundPosition: 'right 0.35rem center',
                                                                backgroundSize: '0.8rem 0.8rem',
                                                                backgroundRepeat: 'no-repeat'
                                                            }}
                                                        >
                                                            <option value="pending" className={isDark ? 'bg-black text-white' : 'bg-white text-gray-900'}>⏳ Pendiente</option>
                                                            <option value="partially_paid" className={isDark ? 'bg-black text-white' : 'bg-white text-gray-900'}>💳 Abono 50%</option>
                                                            <option value="paid" className={isDark ? 'bg-black text-white' : 'bg-white text-gray-900'}>✅ Pagado</option>
                                                            <option value="confirmed" className={isDark ? 'bg-black text-white' : 'bg-white text-gray-900'}>🛡️ Confirmado</option>
                                                            <option value="completed" className={isDark ? 'bg-black text-white' : 'bg-white text-gray-900'}>🏆 Completado</option>
                                                            <option value="cancelled" className={isDark ? 'bg-black text-white' : 'bg-white text-gray-900'}>❌ Cancelado</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => setEditBooking(JSON.parse(JSON.stringify(booking)))}
                                                        className={`p-2 border rounded-[8px] transition-all inline-flex items-center justify-center ${
                                                            isDark ? 'bg-black border-rock text-gray-400 hover:text-[#00d4ff] hover:border-[#00d4ff]' : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-400'
                                                        }`}
                                                        title="Editar reserva"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setSelectedBooking(booking)}
                                                        className={`p-2 border rounded-[8px] transition-all inline-flex items-center justify-center ${
                                                            isDark ? 'bg-black border-rock text-gray-400 hover:text-white hover:border-white' : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-400'
                                                        }`}
                                                    >
                                                        <ChevronRight className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(booking)}
                                                        className="p-2 border rounded-[8px] transition-all inline-flex items-center justify-center bg-red-500/10 border-red-500/30 text-red-500 hover:bg-red-500/20 hover:border-red-500"
                                                        title="Eliminar reserva"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Notification Toast */}
            {notification && createPortal(
                <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[300000] animate-in slide-in-from-top duration-500">
                    <div className={`px-8 py-5 flex items-center gap-4 shadow-2xl border-2 backdrop-blur-xl rounded-[10px] ${
                        notification.type === 'error' ? 'bg-red-600 border-red-400 text-white' : 'bg-[#25D366] border-[#25D366]/50 text-white'
                    }`}>
                        <div className="bg-white/20 p-2 rounded-full">
                            {notification.type === 'error' ? <AlertTriangle className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
                        </div>
                        <div>
                            <p className="text-[12px] font-black uppercase tracking-widest">{notification.message}</p>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Booking Details sliding panel (Drawer) */}
            {selectedBooking && createPortal(
                <div className="fixed inset-0 z-[100000] flex justify-end bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className={`w-full max-w-3xl h-full flex flex-col shadow-2xl border-l animate-in slide-in-from-right duration-400 ${
                        isDark ? 'bg-[#1a1512] border-rock text-white' : 'bg-white border-gray-100 text-gray-900'
                    }`}>
                        {/* Header */}
                        <div className={`p-6 border-b flex items-center justify-between ${
                            isDark ? 'border-rock bg-black/20' : 'border-gray-100 bg-gray-50'
                        }`}>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[8px] font-bold text-gray-500 uppercase tracking-widest font-mono">
                                        ID RESERVA: {selectedBooking.id}
                                    </span>
                                </div>
                                <h2 className="text-2xl font-title uppercase tracking-tight text-[#00d4ff]">
                                    Detalles de Reserva
                                </h2>
                                <p className="text-gray-400 text-[9px] uppercase tracking-widest font-bold mt-1">
                                    Registrado el {new Date(selectedBooking.created_at).toLocaleString('es-CL')}
                                </p>
                            </div>
                            <button 
                                onClick={() => setSelectedBooking(null)}
                                className={`p-2 rounded-full transition-all ${isDark ? 'hover:bg-white/5 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body Scroll */}
                        <div className="flex-1 overflow-y-auto p-8 space-y-8 scrollbar-thin">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className={`border p-4 rounded-[8px] ${isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-100 shadow-sm'}`}>
                                    <div className="text-[8px] font-bold text-gray-500 uppercase tracking-widest mb-1">Expedición</div>
                                    <div className={`font-bold uppercase text-xs truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {getExpedicionName(selectedBooking.expedition_slug)}
                                    </div>
                                    <div className="text-[9px] text-gray-400 mt-0.5">Slug: {selectedBooking.expedition_slug}</div>
                                </div>
                                <div className={`border p-4 rounded-[8px] ${isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-100 shadow-sm'}`}>
                                    <div className="text-[8px] font-bold text-gray-500 uppercase tracking-widest mb-1">Salida / Calendario</div>
                                    <div className={`font-bold uppercase text-xs font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {new Date(selectedBooking.start_date).toLocaleDateString('es-CL', {
                                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                                        })}
                                    </div>
                                </div>
                                <div className={`border p-4 rounded-[8px] ${isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-100 shadow-sm'}`}>
                                    <div className="text-[8px] font-bold text-gray-500 uppercase tracking-widest mb-1">Inversión Recibida</div>
                                    <div className="font-title text-xl text-[#00d4ff]">
                                        USD${Number(selectedBooking.total_price).toLocaleString()}
                                    </div>
                                    <div className="text-[9px] text-gray-400 mt-0.5">Cobro Neto PayPal</div>
                                </div>
                            </div>

                            {/* Payment Status Editor */}
                            <div className={`border p-6 rounded-[8px] ${isDark ? 'bg-black/10 border-rock' : 'bg-gray-50/50 border-gray-100 shadow-sm'}`}>
                                <h3 className={`text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    <CreditCard className="w-4 h-4 text-[#00d4ff]" />
                                    Transacción y Estado
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-400 block">ID Transacción PayPal</label>
                                        <div className="flex items-center gap-2">
                                            <span className={`font-mono text-xs px-3 py-2 rounded border select-all block w-full truncate ${
                                                isDark ? 'text-gray-300 bg-black/40 border-rock/50' : 'text-gray-700 bg-gray-100 border-gray-200'
                                            }`}>
                                                {selectedBooking.paypal_order_id || 'Sin Transacción'}
                                            </span>
                                            {selectedBooking.paypal_order_id && (
                                                <a 
                                                    href={`https://www.paypal.com/activity/payment/${selectedBooking.paypal_order_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`p-2 border rounded transition-all ${isDark ? 'border-rock hover:border-white text-gray-400 hover:text-white bg-black' : 'border-gray-200 text-gray-500 hover:text-gray-900 bg-white'}`}
                                                    title="Ver en PayPal Activity"
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-400 block">Cambiar Estado de la Reserva</label>
                                        <div className="relative">
                                            <select
                                                value={selectedBooking.payment_status}
                                                disabled={updatingId === selectedBooking.id}
                                                onChange={(e) => handleStatusChange(selectedBooking, e.target.value)}
                                                className={`w-full border px-4 py-3 text-xs outline-none focus:border-[#00d4ff] transition-all rounded-[8px] font-bold uppercase tracking-widest cursor-pointer ${
                                                    isDark ? 'bg-black border-rock text-white' : 'bg-white border-gray-200 text-gray-900'
                                                }`}
                                            >
                                                <option value="pending">⏳ Pendiente</option>
                                                <option value="partially_paid">💳 Abono 50%</option>
                                                <option value="paid">✅ Pagado</option>
                                                <option value="confirmed">🛡️ Confirmado</option>
                                                <option value="completed">🏆 Completado</option>
                                                <option value="cancelled">❌ Cancelado</option>
                                            </select>
                                        </div>
                                        <p className="text-[8px] text-gray-500 italic mt-1 leading-relaxed">
                                            * Cambiar el estado actualizará la base de datos y enviará un correo automático al cliente.
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-4 pt-4 border-t border-rock/20">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Servicio de Aeropuerto:</span>
                                        <span className={`px-2 py-0.5 rounded-[10px] text-[8px] font-bold uppercase tracking-widest border ${
                                            selectedBooking.airport_transfer 
                                                ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' 
                                                : 'bg-gray-500/10 text-gray-500 border-gray-500/20'
                                        }`}>
                                            {selectedBooking.airport_transfer ? '✓ Requerido' : 'No Solicitado'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Internal Admin Notes */}
                            {(selectedBooking.internal_notes || selectedBooking.created_by) && (
                                <div className={`border p-6 rounded-[8px] space-y-3 ${isDark ? 'bg-black/10 border-rock' : 'bg-gray-50/50 border-gray-100 shadow-sm'}`}>
                                    <h3 className={`text-xs font-bold uppercase tracking-widest flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        <Info className="w-4 h-4 text-[#00d4ff]" />
                                        Información de Administración
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                        <div>
                                            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Origen de la Reserva</span>
                                            <span className={`px-2 py-0.5 rounded-[10px] text-[8px] font-bold uppercase tracking-widest border ${
                                                selectedBooking.created_by === 'admin' 
                                                    ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-[#00d4ff]/20' 
                                                    : 'bg-green-500/10 text-green-400 border-green-500/20'
                                            }`}>
                                                {selectedBooking.created_by === 'admin' ? '👤 Manual (CRM)' : '🌐 Web Pública'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Políticas Aceptadas</span>
                                            <div className="flex gap-2">
                                                <span className={`px-2 py-0.5 rounded-[10px] text-[8px] font-bold uppercase tracking-widest border ${
                                                    selectedBooking.waiver_accepted ? 'bg-[#25D366]/10 text-[#25D366] border-[#25D366]/20' : 'bg-red-500/10 text-red-500 border-red-500/20'
                                                }`}>
                                                    Términos: {selectedBooking.waiver_accepted ? 'Sí' : 'No'}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-[10px] text-[8px] font-bold uppercase tracking-widest border ${
                                                    selectedBooking.cancellation_policy_accepted ? 'bg-[#25D366]/10 text-[#25D366] border-[#25D366]/20' : 'bg-red-500/10 text-red-500 border-red-500/20'
                                                }`}>
                                                    Cancelación: {selectedBooking.cancellation_policy_accepted ? 'Sí' : 'No'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    {selectedBooking.internal_notes && (
                                        <div className="pt-3 border-t border-rock/20 text-xs">
                                            <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Notas Internas</span>
                                            <p className={`whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{selectedBooking.internal_notes}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Selected Wellness Services */}
                            {selectedBooking.participants?.[0]?.selected_services && selectedBooking.participants[0].selected_services.length > 0 && (
                                <div className={`border p-6 rounded-[8px] ${isDark ? 'bg-black/10 border-rock' : 'bg-gray-50/50 border-gray-100'}`}>
                                    <h3 className={`text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        <TrendingUp className="w-4 h-4 text-[#00d4ff]" />
                                        Servicios Wellness Diseñados (Itinerario)
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedBooking.participants[0].selected_services.map((srv, idx) => (
                                            <span key={idx} className="bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/20 px-3 py-1.5 rounded-[8px] text-[9px] font-bold uppercase tracking-widest">
                                                {srv}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Participants details */}
                            <div className="space-y-4">
                                <h3 className={`text-xs font-bold uppercase tracking-widest border-b pb-2 flex items-center gap-2 ${
                                    isDark ? 'text-white border-rock' : 'text-gray-900 border-gray-200'
                                }`}>
                                    <Users className="w-4 h-4 text-[#00d4ff]" />
                                    Participantes Registrados ({selectedBooking.people_count})
                                </h3>

                                <div className="space-y-6">
                                    {selectedBooking.participants?.map((participant, index) => {
                                        const hasMedicalIssues = participant.health && participant.health.toLowerCase().trim() !== 'ninguna' && participant.health.toLowerCase().trim() !== 'no' && participant.health.toLowerCase().trim() !== 'ninguno';

                                        return (
                                            <div 
                                                key={index} 
                                                className={`border p-6 rounded-[8px] relative overflow-hidden transition-all ${
                                                    isDark ? 'bg-black/20 border-rock hover:border-rock/80' : 'bg-white border-gray-200 shadow-sm'
                                                }`}
                                            >
                                                {/* Badge Count */}
                                                <div className="absolute top-0 right-0 p-4 text-4xl font-title text-white/5 select-none font-bold">
                                                    0{index + 1}
                                                </div>

                                                <div className="flex items-center gap-2 mb-4">
                                                    <span className="w-2.5 h-2.5 bg-[#00d4ff] rounded-full"></span>
                                                    <h4 className={`font-bold uppercase tracking-tight text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                        {participant.fullName}
                                                    </h4>
                                                    {index === 0 && (
                                                        <span className="bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/20 px-1.5 py-0.5 rounded-[10px] text-[7px] font-bold uppercase tracking-widest">
                                                            Líder de Reserva
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Details Grid */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-xs mt-2">
                                                    <div className="space-y-1">
                                                        <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500">RUT / Pasaporte</span>
                                                        <p className={`font-mono font-bold uppercase ${isDark ? 'text-gray-300' : 'text-gray-800'}`}>{participant.passport || 'N/A'}</p>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500">Fecha de Nacimiento</span>
                                                        <p className={`font-mono ${isDark ? 'text-gray-300' : 'text-gray-800'}`}>
                                                            {participant.dob ? new Date(participant.dob).toLocaleDateString('es-CL', {
                                                                year: 'numeric', month: 'short', day: 'numeric'
                                                            }) : 'N/A'}
                                                        </p>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500">Equipamiento (Tallas)</span>
                                                        <p className={`font-bold uppercase ${isDark ? 'text-gray-300' : 'text-gray-800'}`}>
                                                            Calzado: {participant.shoeSize || 'N/A'} <br /> Ropa: {participant.clothingSize || 'N/A'}
                                                        </p>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500">Contacto de Emergencia</span>
                                                        <div className={`leading-tight ${isDark ? 'text-gray-300' : 'text-gray-800'}`}>
                                                            <div className="font-bold text-[10px] uppercase truncate">{participant.emergencyName || 'N/A'}</div>
                                                            <div className="font-mono text-[9px] text-[#00d4ff] mt-0.5 flex items-center gap-1">
                                                                <Phone className="w-2.5 h-2.5" />
                                                                {participant.emergencyPhone || 'N/A'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs mt-4 pt-4 border-t border-rock/10">
                                                    <div className="space-y-1">
                                                        <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500 block">Restricciones Alimentarias</span>
                                                        <p className={`italic ${
                                                            participant.diet 
                                                                ? 'font-medium ' + (isDark ? 'text-gray-200' : 'text-gray-800') 
                                                                : (isDark ? 'text-gray-600' : 'text-gray-400')
                                                        }`}>
                                                            {participant.diet || 'Ninguna'}
                                                        </p>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500 block">Condición Médica / Alergias</span>
                                                        <div className={`p-3 rounded border text-[11px] leading-relaxed ${
                                                            hasMedicalIssues 
                                                                ? 'bg-red-500/10 border-red-500/30 text-red-400 font-medium' 
                                                                : (isDark ? 'bg-black/30 border-rock/50 text-gray-500' : 'bg-gray-100 border-gray-200 text-gray-600') + ' italic'
                                                        }`}>
                                                            {participant.health || 'Ninguna condición registrada.'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Email (Visible on lead) */}
                                                {participant.email && (
                                                    <div className="mt-4 pt-4 border-t border-rock/10 flex items-center gap-2">
                                                        <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500">Correo Electrónico de Contacto:</span>
                                                        <a 
                                                            href={`mailto:${participant.email}`}
                                                            className="text-xs text-[#00d4ff] hover:underline font-mono"
                                                        >
                                                            {participant.email}
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className={`p-6 border-t flex items-center justify-between gap-4 ${
                            isDark ? 'border-rock bg-black/20' : 'border-gray-100 bg-gray-50'
                        }`}>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setEditBooking(JSON.parse(JSON.stringify(selectedBooking)))}
                                    className={`flex items-center gap-2 px-5 py-3 border text-xs font-bold uppercase tracking-widest transition-all rounded-[8px] ${
                                        isDark ? 'bg-black border-[#00d4ff]/40 text-[#00d4ff] hover:bg-[#00d4ff]/10' : 'bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100'
                                    }`}
                                >
                                    <Pencil className="w-3.5 h-3.5" />
                                    Editar
                                </button>
                                <button
                                    onClick={() => setDeleteConfirm(selectedBooking)}
                                    className="flex items-center gap-2 px-5 py-3 border text-xs font-bold uppercase tracking-widest transition-all rounded-[8px] bg-red-500/10 border-red-500/30 text-red-500 hover:bg-red-500/20 hover:border-red-500"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Eliminar
                                </button>
                            </div>
                            <button
                                onClick={() => setSelectedBooking(null)}
                                className={`px-6 py-3 border text-xs font-bold uppercase tracking-widest transition-all rounded-[8px] ${
                                    isDark ? 'bg-black border-rock text-gray-400 hover:text-white hover:border-white' : 'bg-gray-100 border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-200'
                                }`}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && createPortal(
                <div className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className={`w-full max-w-md mx-4 border rounded-[12px] shadow-2xl animate-in zoom-in-95 duration-200 ${
                        isDark ? 'bg-[#1a1512] border-red-500/30' : 'bg-white border-red-200'
                    }`}>
                        <div className="p-6 border-b border-red-500/20 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                                <Trash2 className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <h3 className={`font-bold text-sm uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>Eliminar Reserva</h3>
                                <p className="text-[10px] text-red-400 uppercase tracking-widest font-bold">Acción irreversible</p>
                            </div>
                        </div>
                        <div className="p-6">
                            <p className={`text-sm leading-relaxed mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                ¿Confirmas que deseas <strong>eliminar permanentemente</strong> esta reserva?
                            </p>
                            <div className={`mt-4 p-3 rounded-[8px] border text-xs ${
                                isDark ? 'bg-black/40 border-rock' : 'bg-gray-50 border-gray-200'
                            }`}>
                                <div className={`font-bold uppercase tracking-tight mb-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {deleteConfirm.participants?.[0]?.fullName || 'Sin Nombre'}
                                </div>
                                <div className="text-gray-500">{getExpedicionName(deleteConfirm.expedition_slug)} · {new Date(deleteConfirm.start_date).toLocaleDateString('es-CL')}</div>
                                <div className="text-[10px] font-mono text-gray-600 mt-1">ID: {deleteConfirm.id}</div>
                            </div>
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                disabled={deleting}
                                className={`flex-1 px-4 py-3 border text-xs font-bold uppercase tracking-widest rounded-[8px] transition-all ${
                                    isDark ? 'bg-black border-rock text-gray-400 hover:text-white hover:border-white' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
                                } disabled:opacity-50`}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleDeleteBooking(deleteConfirm)}
                                disabled={deleting}
                                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-widest rounded-[8px] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {deleting ? (
                                    <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Eliminando...</>
                                ) : (
                                    <><Trash2 className="w-3.5 h-3.5" /> Confirmar Eliminar</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Edit Booking Modal */}
            {editBooking && createPortal(
                <div className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200 p-4">
                    <div className={`w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border rounded-[12px] shadow-2xl animate-in zoom-in-95 duration-200 ${
                        isDark ? 'bg-[#1a1512] border-[#00d4ff]/30' : 'bg-white border-blue-200'
                    }`}>
                        {/* Modal Header */}
                        <div className={`p-6 border-b flex items-center justify-between flex-shrink-0 ${
                            isDark ? 'border-rock bg-black/20' : 'border-gray-100 bg-gray-50'
                        }`}>
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-[#00d4ff]/10 border border-[#00d4ff]/30 flex items-center justify-center">
                                    <Pencil className="w-4 h-4 text-[#00d4ff]" />
                                </div>
                                <div>
                                    <h3 className={`font-bold text-sm uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>Editar Reserva</h3>
                                    <p className="text-[9px] text-gray-500 font-mono">{editBooking.id}</p>
                                </div>
                            </div>
                            <button onClick={() => setEditBooking(null)} className="p-2 rounded-full text-gray-400 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 max-h-[65vh] overflow-y-auto p-6 space-y-8">

                            {/* Section: Datos Principales */}
                            <div>
                                <h4 className={`text-[10px] font-bold uppercase tracking-widest mb-4 pb-2 border-b ${
                                    isDark ? 'text-[#00d4ff] border-rock' : 'text-blue-600 border-gray-200'
                                }`}>Datos Principales de la Reserva</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Fecha de Salida</label>
                                        <input
                                            type="date"
                                            value={editBooking.start_date}
                                            onChange={e => setEditBooking(prev => prev ? { ...prev, start_date: e.target.value } : null)}
                                            className={`w-full border px-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Número de Personas</label>
                                        <input
                                            type="number"
                                            min={1}
                                            value={editBooking.people_count}
                                            onChange={e => setEditBooking(prev => prev ? { ...prev, people_count: Number(e.target.value) } : null)}
                                            className={`w-full border px-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Precio Total (USD)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            value={editBooking.total_price}
                                            onChange={e => setEditBooking(prev => prev ? { ...prev, total_price: Number(e.target.value) } : null)}
                                            className={`w-full border px-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Servicio de Aeropuerto</label>
                                        <select
                                            value={editBooking.airport_transfer ? 'yes' : 'no'}
                                            onChange={e => setEditBooking(prev => prev ? { ...prev, airport_transfer: e.target.value === 'yes' } : null)}
                                            className={`w-full border px-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                                            }`}
                                        >
                                            <option value="no">No solicitado</option>
                                            <option value="yes">✓ Requerido</option>
                                        </select>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Notas Internas de Administración</label>
                                        <textarea
                                            rows={2}
                                            value={editBooking.internal_notes || ''}
                                            onChange={e => setEditBooking(prev => prev ? { ...prev, internal_notes: e.target.value } : null)}
                                            placeholder="Ingresa notas internas sobre el cliente o coordinación..."
                                            className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all resize-none ${
                                                isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section: Participantes */}
                            <div>
                                <h4 className={`text-[10px] font-bold uppercase tracking-widest mb-4 pb-2 border-b ${
                                    isDark ? 'text-[#00d4ff] border-rock' : 'text-blue-600 border-gray-200'
                                }`}>Participantes</h4>
                                <div className="space-y-6">
                                    {editBooking.participants?.map((p, idx) => (
                                        <div key={idx} className={`border p-5 rounded-[10px] ${
                                            isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-200'
                                        }`}>
                                            <div className="flex items-center gap-2 mb-4">
                                                <span className="w-2 h-2 bg-[#00d4ff] rounded-full"></span>
                                                <span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-700'}`}>
                                                    Participante {idx + 1}{idx === 0 ? ' — Líder de Reserva' : ''}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {([
                                                    { key: 'fullName', label: 'Nombre Completo' },
                                                    { key: 'email', label: 'Email (solo lead)' },
                                                    { key: 'passport', label: 'RUT / Pasaporte' },
                                                    { key: 'dob', label: 'Fecha de Nacimiento', type: 'date' },
                                                    { key: 'shoeSize', label: 'Talla Calzado' },
                                                    { key: 'clothingSize', label: 'Talla Ropa' },
                                                    { key: 'emergencyName', label: 'Contacto de Emergencia' },
                                                    { key: 'emergencyPhone', label: 'Teléfono Emergencia' },
                                                ] as { key: keyof Participant; label: string; type?: string }[]).map(({ key, label, type }) => (
                                                    <div key={key}>
                                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">{label}</label>
                                                        <input
                                                            type={type || 'text'}
                                                            value={(p[key] as string) || ''}
                                                            onChange={e => {
                                                                const updated = editBooking.participants.map((pp, i) =>
                                                                    i === idx ? { ...pp, [key]: e.target.value } : pp
                                                                );
                                                                setEditBooking(prev => prev ? { ...prev, participants: updated } : null);
                                                            }}
                                                            className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                                isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                            }`}
                                                        />
                                                    </div>
                                                ))}
                                                {/* Diet - full width */}
                                                <div className="sm:col-span-2">
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Restricciones Alimentarias</label>
                                                    <input
                                                        type="text"
                                                        value={p.diet || ''}
                                                        onChange={e => {
                                                            const updated = editBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, diet: e.target.value } : pp
                                                            );
                                                            setEditBooking(prev => prev ? { ...prev, participants: updated } : null);
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>
                                                {/* Health - full width */}
                                                <div className="sm:col-span-2">
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Condición Médica / Alergias</label>
                                                    <textarea
                                                        rows={2}
                                                        value={p.health || ''}
                                                        onChange={e => {
                                                            const updated = editBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, health: e.target.value } : pp
                                                            );
                                                            setEditBooking(prev => prev ? { ...prev, participants: updated } : null);
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all resize-none ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className={`p-6 border-t flex items-center justify-end gap-3 flex-shrink-0 ${
                            isDark ? 'border-rock bg-black/20' : 'border-gray-100 bg-gray-50'
                        }`}>
                            <button
                                onClick={() => setEditBooking(null)}
                                disabled={saving}
                                className={`px-5 py-3 border text-xs font-bold uppercase tracking-widest rounded-[8px] transition-all ${
                                    isDark ? 'bg-black border-rock text-gray-400 hover:text-white hover:border-white' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
                                } disabled:opacity-50`}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-3 bg-[#00d4ff] hover:bg-[#00b8e6] text-black text-xs font-bold uppercase tracking-widest rounded-[8px] transition-all disabled:opacity-50"
                            >
                                {saving ? (
                                    <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Guardando...</>
                                ) : (
                                    <><Save className="w-3.5 h-3.5" /> Guardar Cambios</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* New Booking Modal */}
            {showNewBookingModal && createPortal(
                <div className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200 p-4">
                    <div className={`w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border rounded-[12px] shadow-2xl animate-in zoom-in-95 duration-200 ${
                        isDark ? 'bg-[#1a1512] border-[#00d4ff]/30' : 'bg-white border-blue-200'
                    }`}>
                        {/* Modal Header */}
                        <div className={`p-6 border-b flex items-center justify-between flex-shrink-0 ${
                            isDark ? 'border-rock bg-black/20' : 'border-gray-100 bg-gray-50'
                        }`}>
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-[#00d4ff]/10 border border-[#00d4ff]/30 flex items-center justify-center">
                                    <Plus className="w-5 h-5 text-[#00d4ff]" />
                                </div>
                                <div>
                                    <h3 className={`font-title text-base uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>Nueva Reserva Manual</h3>
                                    <p className="text-[9px] text-gray-500 uppercase tracking-widest font-bold">Registrar venta o reserva offline</p>
                                </div>
                            </div>
                            <button onClick={() => setShowNewBookingModal(false)} className="p-2 rounded-full text-gray-400 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 max-h-[65vh] overflow-y-auto p-6 space-y-8">

                            {/* Section: Datos Principales */}
                            <div>
                                <h4 className={`text-[10px] font-bold uppercase tracking-widest mb-4 pb-2 border-b ${
                                    isDark ? 'text-[#00d4ff] border-rock' : 'text-blue-600 border-gray-200'
                                }`}>1. Datos de la Reserva</h4>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                    {/* Expedition selection */}
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Expedición <span className="text-red-500">*</span></label>
                                        <select
                                            value={newBooking.expedition_slug}
                                            onChange={e => handleNewBookingFieldChange('expedition_slug', e.target.value)}
                                            className={`w-full border px-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all cursor-pointer ${
                                                isDark ? 'bg-black border-rock text-white' : 'bg-white border-gray-200 text-gray-900'
                                            }`}
                                        >
                                            <option value="">Selecciona Expedición...</option>
                                            {sortExpeditions(expeditions || []).map(exp => (
                                                <option key={exp.id} value={exp.id}>
                                                    {exp.name_es || exp.name_en}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Start Date */}
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Fecha de Salida <span className="text-red-500">*</span></label>
                                        <input
                                            type="date"
                                            value={newBooking.start_date}
                                            onChange={e => handleNewBookingFieldChange('start_date', e.target.value)}
                                            className={`w-full border px-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                                            }`}
                                        />
                                    </div>

                                    {/* People Count */}
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Número de Personas</label>
                                        <input
                                            type="number"
                                            min={1}
                                            value={newBooking.people_count}
                                            onChange={e => handleNewBookingFieldChange('people_count', Number(e.target.value))}
                                            className={`w-full border px-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                                            }`}
                                        />
                                    </div>

                                    {/* Auto calculated price with manual override */}
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-[#00d4ff] block mb-1">Precio Total (USD) [Auto/Editable]</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-bold">$</span>
                                            <input
                                                type="number"
                                                min={0}
                                                value={newBooking.total_price}
                                                onChange={e => handleNewBookingFieldChange('total_price', Number(e.target.value))}
                                                className={`w-full border pl-7 pr-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] font-bold transition-all ${
                                                    isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                                                }`}
                                            />
                                        </div>
                                    </div>

                                    {/* Airport Transfer */}
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Servicio de Aeropuerto (+$50)</label>
                                        <select
                                            value={newBooking.airport_transfer ? 'yes' : 'no'}
                                            onChange={e => handleNewBookingFieldChange('airport_transfer', e.target.value === 'yes')}
                                            className={`w-full border px-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                                            }`}
                                        >
                                            <option value="no">No solicitado</option>
                                            <option value="yes">✓ Requerido (+$50)</option>
                                        </select>
                                    </div>

                                    {/* Payment Status (defaults to pending) */}
                                    <div>
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Estado de Pago Inicial</label>
                                        <select
                                            value={newBooking.payment_status}
                                            onChange={e => handleNewBookingFieldChange('payment_status', e.target.value)}
                                            className={`w-full border px-3 py-2.5 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                                            }`}
                                        >
                                            <option value="pending">⏳ Pendiente</option>
                                            <option value="partially_paid">💳 Abono 50%</option>
                                            <option value="paid">✅ Pagado</option>
                                            <option value="confirmed">🛡️ Confirmado</option>
                                        </select>
                                    </div>

                                    {/* Internal Notes */}
                                    <div className="md:col-span-3">
                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Notas Internas</label>
                                        <textarea
                                            rows={2}
                                            value={newBooking.internal_notes}
                                            onChange={e => handleNewBookingFieldChange('internal_notes', e.target.value)}
                                            placeholder="Detalles sobre el pago, contacto por WhatsApp, requerimientos especiales..."
                                            className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all resize-none ${
                                                isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section: Participantes */}
                            <div>
                                <h4 className={`text-[10px] font-bold uppercase tracking-widest mb-4 pb-2 border-b ${
                                    isDark ? 'text-[#00d4ff] border-rock' : 'text-blue-600 border-gray-200'
                                }`}>2. Datos de Pasajeros ({newBooking.people_count})</h4>

                                <div className="space-y-6">
                                    {newBooking.participants.map((p, idx) => (
                                        <div key={idx} className={`border p-6 rounded-[10px] relative overflow-hidden ${
                                            isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-200'
                                        }`}>
                                            {/* Decorative Number Tag */}
                                            <div className="absolute top-0 right-0 p-4 text-5xl font-title text-white/5 select-none font-bold">
                                                0{idx + 1}
                                            </div>

                                            <div className="flex items-center gap-2 mb-4 relative z-10">
                                                <span className="w-2.5 h-2.5 bg-[#00d4ff] rounded-full"></span>
                                                <span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-700'}`}>
                                                    Participante {idx + 1}{idx === 0 ? ' — Líder de Reserva (Recibe los Correos)' : ''}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 relative z-10">
                                                {/* Nombre Completo */}
                                                <div>
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">
                                                        Nombre Completo <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        required
                                                        placeholder="Ej: Juan Pérez"
                                                        value={p.fullName}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, fullName: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>

                                                {/* Email */}
                                                <div>
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">
                                                        Email {idx === 0 && <span className="text-[#00d4ff] font-bold">(Líder)</span>}
                                                    </label>
                                                    <input
                                                        type="email"
                                                        placeholder={idx === 0 ? "juan@email.com (Necesario para enviar correo)" : "opcional@email.com"}
                                                        value={p.email || ''}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, email: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>

                                                {/* RUT / Passport */}
                                                <div>
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">RUT / Pasaporte</label>
                                                    <input
                                                        type="text"
                                                        placeholder="RUT o Pasaporte"
                                                        value={p.passport}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, passport: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>

                                                {/* Date of birth */}
                                                <div>
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Fecha Nacimiento</label>
                                                    <input
                                                        type="date"
                                                        value={p.dob}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, dob: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                            isDark ? 'bg-black border-rock text-white' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>

                                                {/* Shoe size */}
                                                <div>
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Talla Calzado</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Ej: 39 EUR / 8.5 US"
                                                        value={p.shoeSize}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, shoeSize: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>

                                                {/* Clothing size */}
                                                <div>
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Talla Ropa</label>
                                                    <select
                                                        value={p.clothingSize}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, clothingSize: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all cursor-pointer ${
                                                            isDark ? 'bg-black border-rock text-white' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    >
                                                        <option value="">Selecciona talla...</option>
                                                        <option value="XS">XS</option>
                                                        <option value="S">S</option>
                                                        <option value="M">M</option>
                                                        <option value="L">L</option>
                                                        <option value="XL">XL</option>
                                                        <option value="XXL">XXL</option>
                                                    </select>
                                                </div>

                                                {/* Emergency Name */}
                                                <div>
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Contacto Emergencia</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Nombre y relación"
                                                        value={p.emergencyName}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, emergencyName: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>

                                                {/* Emergency Phone */}
                                                <div>
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Teléfono Emergencia</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Ej: +56 9 1234 5678"
                                                        value={p.emergencyPhone}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, emergencyPhone: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>

                                                {/* Diet */}
                                                <div className="sm:col-span-2">
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Restricciones Alimentarias (Diet)</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Vegetariano, vegano, alergia al gluten, lactosa, etc. o 'Ninguna'"
                                                        value={p.diet}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, diet: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>

                                                {/* Health */}
                                                <div className="sm:col-span-2">
                                                    <label className="text-[9px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Condición Médica / Alergias relevantes</label>
                                                    <textarea
                                                        rows={2}
                                                        placeholder="Asma, problemas cardíacos, lesiones recientes, alergias a picaduras, etc. o 'Ninguna'"
                                                        value={p.health}
                                                        onChange={e => {
                                                            const updated = newBooking.participants.map((pp, i) =>
                                                                i === idx ? { ...pp, health: e.target.value } : pp
                                                            );
                                                            setNewBooking(prev => ({ ...prev, participants: updated }));
                                                        }}
                                                        className={`w-full border px-3 py-2 text-xs rounded-[8px] outline-none focus:border-[#00d4ff] transition-all resize-none ${
                                                            isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900'
                                                        }`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className={`p-6 border-t flex items-center justify-end gap-3 flex-shrink-0 ${
                            isDark ? 'border-rock bg-black/20' : 'border-gray-100 bg-gray-50'
                        }`}>
                            <button
                                onClick={() => setShowNewBookingModal(false)}
                                disabled={saving}
                                className={`px-5 py-3 border text-xs font-bold uppercase tracking-widest rounded-[8px] transition-all ${
                                    isDark ? 'bg-black border-rock text-gray-400 hover:text-white hover:border-white' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
                                } disabled:opacity-50`}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleCreateBooking(false)}
                                disabled={saving}
                                className={`flex items-center gap-2 px-5 py-3 border text-xs font-bold uppercase tracking-widest rounded-[8px] transition-all disabled:opacity-50 ${
                                    isDark ? 'bg-black border-rock text-gray-300 hover:text-white hover:border-white' : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <Save className="w-3.5 h-3.5" />
                                Crear Reserva
                            </button>
                            <button
                                onClick={() => handleCreateBooking(true)}
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-3 bg-[#00d4ff] hover:bg-[#00b8e6] text-black text-xs font-bold uppercase tracking-widest rounded-[8px] transition-all disabled:opacity-50 shadow-[0_4px_12px_rgba(0,212,255,0.2)]"
                            >
                                <Send className="w-3.5 h-3.5" />
                                {saving ? 'Guardando...' : 'Crear y Enviar Email'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default BookingsManager;
